import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  exerciceStatutEffectif,
  isExerciceStatutModifiable,
  type ExerciceStatut,
} from "../lib/exercices";

function useExerciceIdPourEcriture(
  ecritureComptableId: string | null | undefined
): { exerciceId: string | null; resolving: boolean } {
  const [exerciceId, setExerciceId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!ecritureComptableId) {
      setExerciceId(null);
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    void (async () => {
      const { data: ec, error } = await supabase
        .from("ecritures_comptables")
        .select("dossier_id, date_ecriture")
        .eq("id", ecritureComptableId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !ec) {
        setExerciceId(null);
        setResolving(false);
        return;
      }
      const d = String(ec.date_ecriture).slice(0, 10);
      const { data: ex } = await supabase
        .from("exercices_comptables")
        .select("id")
        .eq("dossier_id", ec.dossier_id as string)
        .lte("date_debut", d)
        .gte("date_fin", d)
        .maybeSingle();
      if (cancelled) return;
      setExerciceId((ex?.id as string) ?? null);
      setResolving(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ecritureComptableId]);

  return { exerciceId, resolving };
}

export interface UseExerciceEditableResult {
  loading: boolean;
  isEditable: boolean;
  statut: ExerciceStatut | "";
  message: string;
}

function messagePourStatut(st: ExerciceStatut): string {
  if (st === "fige") {
    return "Cet exercice est figé. Les modifications ne sont pas autorisées.";
  }
  if (st === "cloture") {
    return "Cet exercice est clôturé. Les modifications ne sont pas autorisées.";
  }
  return "";
}

/**
 * Indique si la saisie comptable est autorisée pour l’exercice donné (statut ouvert ou réouvert).
 */
export function useExerciceEditable(exerciceId: string | null | undefined): UseExerciceEditableResult {
  const [loading, setLoading] = useState(Boolean(exerciceId));
  const [statut, setStatut] = useState<ExerciceStatut | "">("");
  const [isEditable, setIsEditable] = useState(true);

  useEffect(() => {
    if (!exerciceId) {
      setLoading(false);
      setStatut("");
      setIsEditable(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from("exercices_comptables")
        .select("statut, cloture")
        .eq("id", exerciceId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setStatut("");
        setIsEditable(true);
        setLoading(false);
        return;
      }
      const st = exerciceStatutEffectif(data as { statut?: string | null; cloture?: boolean | null });
      setStatut(st);
      setIsEditable(isExerciceStatutModifiable(st));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [exerciceId]);

  const message = isEditable ? "" : messagePourStatut(statut || "cloture");

  return { loading, isEditable, statut, message };
}

/** Résout l’exercice à partir d’une écriture comptable (dossier + date). */
export function useExerciceEditableForEcriture(
  ecritureComptableId: string | null | undefined
): UseExerciceEditableResult {
  const { exerciceId, resolving } = useExerciceIdPourEcriture(ecritureComptableId);
  const inner = useExerciceEditable(exerciceId);
  return {
    loading: resolving || inner.loading,
    isEditable: resolving ? false : inner.isEditable,
    statut: inner.statut,
    message: inner.message,
  };
}
